import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { PlusTag } from "../../../../components/PlusTag";
import constants from "../../../../constants";
import { useSafeGoBack } from "../../../../hooks/SafeGoBack";
import CheckIcon from "../../../../icons/Check";
import { classNames } from "../../../../utils/styles";
import { Metric } from "../../utils";
import { FakeChart } from "../FakeChart";
import { Benefits, ChartArea, Hero, LockedPreviewBox, Metrics } from "./styles";

const metricTypes: Metric["type"][] = ["access", "visitors", "clicks"];

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

type Props = {
  metricType: Metric["type"];
  days: number;
  siteId?: string;
};

export const LockedPreview = ({ metricType, days, siteId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const safeGoBack = useSafeGoBack();

  return (
    <LockedPreviewBox>
      <Hero>
        <PlusTag>✦ Keepo Plus</PlusTag>
        <h2>{t("pages.analytics.lockedPreview.subtitle")}</h2>
      </Hero>

      {/* Locked metric tiles: the numbers exist, they're just not shown */}
      <Metrics>
        {metricTypes.map(type => (
          <div key={type} className={classNames("metric", { active: type === metricType })}>
            <span className="label">{t(`pages.analytics.metrics.${type}`)}</span>
            <span className="value">
              <LockIcon />
              •••
            </span>
          </div>
        ))}
      </Metrics>

      <ChartArea>
        <span className="sample">{t("pages.analytics.lockedPreview.sample")}</span>
        <FakeChart days={days} color="rgba(87, 242, 139, 0.45)" />
      </ChartArea>

      <Benefits>
        {(["realtime", "visitors", "clicks"] as const).map(feature => (
          <li key={feature}>
            <span className="check">
              <CheckIcon color="currentColor" />
            </span>
            {t(`pages.analytics.lockedPreview.features.${feature}`)}
          </li>
        ))}
      </Benefits>

      <div className="actions">
        <button type="button" className="cta" onClick={() => navigate(`/subscription/features?site=${siteId}`)}>
          {t("pages.analytics.lockedPreview.unlockButton", { price: constants.price.year.perMonth })}
        </button>
        <button type="button" className="later" onClick={safeGoBack}>
          {t("pages.analytics.lockedPreview.maybeLater")}
        </button>
      </div>
    </LockedPreviewBox>
  );
};
